import * as React from 'react';
import { callRpc, useLiveQuery, type LiveState } from './rpc';

/**
 * The current agency's feature flags (spec operator-panel-v2 T2.6, 0239 `my_agency_flags`): every
 * registry key with the value the operator set for this agency, or null when the flag is at its
 * default. The hook exposes typed readers with a fallback: a consumer asks `isEnabled('portal.chat_enabled', true)`
 * and gets the compiled default until the operator overrides it. No consumer is wired yet
 * (docs/contrato-portal.md section 7.34); this hook is the one place a later screen reads a flag from.
 *
 * Standalone on purpose: AgencyProvider reads tables, not RPCs, and one more read there is not a
 * one-line change. A member of another agency, a driver or a suspended agency reads zero rows, which
 * this hook renders as "every flag at its default".
 */

export type AgencyFlagValue = boolean | number | null;

export interface AgencyFlagRow {
  key: string;
  value_type: 'boolean' | 'int';
  value: unknown;
}

export type AgencyFlagMap = Record<string, AgencyFlagValue>;

/** The rows as the map the readers use: only the set flags, booleans and integers kept as such. */
export function flagMap(rows: readonly AgencyFlagRow[] | null | undefined): AgencyFlagMap {
  const out: AgencyFlagMap = {};
  for (const r of rows ?? []) {
    if (typeof r.value === 'boolean') out[r.key] = r.value;
    else if (typeof r.value === 'number' && Number.isFinite(r.value)) out[r.key] = r.value;
    else if (typeof r.value === 'string' && /^-?\d+$/.test(r.value)) out[r.key] = Number(r.value);
  }
  return out;
}

export interface UseAgencyFlags {
  status: LiveState<AgencyFlagMap>['status'];
  /** The flags the operator set for this agency (a key at its default is absent). */
  flags: AgencyFlagMap;
  /** A boolean flag, or `fallback` while loading, on error, or when the flag is at its default. */
  isEnabled(key: string, fallback: boolean): boolean;
  /** An integer flag, or `fallback` while loading, on error, or when the flag is at its default. */
  intValue(key: string, fallback: number): number;
  reload(): void;
}

export function useAgencyFlags(): UseAgencyFlags {
  const state = useLiveQuery<AgencyFlagMap>(async (client, agencyId) => {
    const res = await callRpc<AgencyFlagRow[]>(client, 'my_agency_flags', { p_agency: agencyId });
    if (res.error) return { error: res.error };
    return { data: flagMap(res.data) };
  }, []);
  const flags = React.useMemo(() => (state.status === 'ready' ? state.data : {}), [state]);
  return React.useMemo(
    () => ({
      status: state.status,
      flags,
      isEnabled: (key, fallback) => (typeof flags[key] === 'boolean' ? (flags[key] as boolean) : fallback),
      intValue: (key, fallback) => (typeof flags[key] === 'number' ? (flags[key] as number) : fallback),
      reload: state.reload,
    }),
    [state.status, state.reload, flags],
  );
}
